/**
 * @file 学びメモの Markdown を表示用の HTML にする。
 *
 * @what: marked で HTML にし、DOMPurify で消毒してから返す (リンクは新しいタブで開く)。
 *   mermaid のコードの欄の1行目に大きさ (```mermaid 60% / ```mermaid 400px) があれば、
 *   `<pre data-mermaid-size>` に写す。図にするときに hooks/useMermaidBlocks.ts が読む。
 * @why: ノートは家族が書く Markdown で、プレビューは innerHTML で出すしかない。
 *   消毒を必ずこの関数に通すことで、script などの混入をここ 1 か所で遮断する。
 * @failure: 変換の失敗は素の文字列をエスケープ表示に落とす (書いたものが消えて見える方が
 *   こわいので、装飾を諦めて中身は必ず出す)。
 */

import DOMPurify from "dompurify";
import { marked, Renderer, type Tokens } from "marked";

marked.setOptions({ gfm: true, breaks: true });

// ノート内のリンクは別タブで開く (ビューワの上で画面遷移すると書きかけが飛ぶため)。
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A") {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noreferrer noopener");
  }
});

/**
 * mermaid の欄の大きさの書き方を、図の幅にする。割合は1〜100%、幅は50〜4000px で、それ以外は
 * 無視する (欄の幅に合わせる)。
 */
export function mermaidSizeOf(lang: string | undefined): string | null {
  const m = /^mermaid\s+(\S+)\s*$/.exec(lang ?? "");
  if (m === null) return null;
  const pct = /^(\d{1,3})%$/.exec(m[1]!);
  if (pct) {
    const n = Number(pct[1]);
    return n >= 1 && n <= 100 ? `${n}%` : null;
  }
  const px = /^(\d{2,4})px$/.exec(m[1]!);
  if (px) {
    const n = Number(px[1]);
    return n >= 50 && n <= 4000 ? `${n}px` : null;
  }
  return null;
}

// 学びメモだけの書き方なので、共有の marked には足さず、この関数の呼び出しにだけ渡す
const noteRenderer = new Renderer();
const renderCode = noteRenderer.code.bind(noteRenderer);
noteRenderer.code = (token: Tokens.Code) => {
  const html = renderCode(token);
  const size = mermaidSizeOf(token.lang);
  return size === null ? html : html.replace(/^<pre>/, `<pre data-mermaid-size="${size}">`);
};

function escapeHtml(text: string): string {
  return text.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
}

// DOMPurify の既定の許可 URI に blob: と note-image: を足したもの。
// ※なぜなら既定は https 等しか許さず、プレビューの画像 (blob URL に解決済み) と
// 未解決の参照 (note-image:<id> をそのまま見せる) の src が消えてしまうためである。
// どちらも遷移先を持たない参照で、script 等の消毒はこれまでどおり効く。
const ALLOWED_URI_REGEXP =
  /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp|blob|note-image):|[^a-z]|[a-z+.-]+(?:[^a-z+.:-]|$))/i;

export function renderMarkdown(text: string): string {
  try {
    const html = marked.parse(text, { async: false, renderer: noteRenderer });
    return DOMPurify.sanitize(html, { ALLOWED_URI_REGEXP });
  } catch (err) {
    console.error("[study] failed to render markdown", err);
    return `<pre>${escapeHtml(text)}</pre>`;
  }
}
